import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import SherDineNav from './SherDineNav';
import SideNav from './SideNav';
import TopRootDiv from './TopRootDiv';
import { DBContext } from './DBContext';
import { exportToCSV } from '../utils/exportUtils';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt     = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const COLORS = ['#0008B4','#57BEB5','#AE59DC','#E8821A','#63B946','#c30000','#2D7DD2','#F5A623'];

const STAFF_COLS = [
    { key: 'staffName',     label: 'Staff / Waiter'  },
    { key: 'billCount',     label: 'Bills'            },
    { key: 'totalNet',      label: 'Net Revenue (₹)' },
    { key: 'avgBill',       label: 'Avg Bill (₹)'    },
    { key: 'totalDiscount', label: 'Discount (₹)'    },
    { key: 'dineInCount',   label: 'Dine In'          },
    { key: 'takeAwayCount', label: 'Take Away'        },
    { key: 'cashTotal',     label: 'Cash (₹)'        },
    { key: 'cardTotal',     label: 'Card (₹)'        },
    { key: 'onlineTotal',   label: 'Online (₹)'      },
];

// ─── Print View ──────────────────────────────────────────────────────────────
const PrintView = React.forwardRef(({ data, dates, summary }, ref) => (
    <div ref={ref} style={{ padding: 20, fontFamily: 'Arial, sans-serif', fontSize: 11 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Staff Performance Report</h2>
        <p style={{ color: '#666', marginBottom: 14 }}>
            {dates.sdate && dates.edate ? `${dates.sdate}  →  ${dates.edate}` : 'Today'}
        </p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {summary.map(({ label, value }) => (
                <div key={label} style={{ background: '#f5f5f5', borderRadius: 6, padding: '6px 14px' }}>
                    <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0008B4' }}>{value}</div>
                </div>
            ))}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
                <tr style={{ background: '#0008B4', color: '#fff' }}>
                    {STAFF_COLS.map(c => (
                        <th key={c.key} style={{ padding: '5px 8px', textAlign: 'left', fontSize: 10 }}>{c.label}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                        {STAFF_COLS.map(c => (
                            <td key={c.key} style={{ padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: 10 }}>
                                {['totalNet','avgBill','totalDiscount','cashTotal','cardTotal','onlineTotal'].includes(c.key)
                                    ? fmt(row[c.key]) : (row[c.key] ?? '')}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
));

// ─── Main Component ──────────────────────────────────────────────────────────
function StaffPerformance() {
    const { logginedDB } = useContext(DBContext);
    const apiBaseUrl     = process.env.REACT_APP_API_BASE_URL;

    const [sideNav, setSideNav] = useState(false);
    const [falseState, setFalse] = useState(null);
    const [dates, setDates]     = useState({ sdate: todayISO(), edate: todayISO() });
    const [data, setData]       = useState([]);
    const [loading, setLoading] = useState(false);

    const printRef = useRef(null);
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Staff_Performance_${dates.sdate}_${dates.edate}`,
    });

    const fetchData = useCallback(async () => {
        if (!logginedDB) return;
        setLoading(true);
        try {
            const res = await axios.get(
                `${apiBaseUrl}/StaffPerformance/${logginedDB}?sdate=${dates.sdate}&edate=${dates.edate}`
            );
            setData(res.data || []);
        } catch (e) {
            console.error('StaffPerformance fetch error:', e);
        } finally {
            setLoading(false);
        }
    }, [logginedDB, dates, apiBaseUrl]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── aggregates ──
    const totalBills    = data.reduce((s, r) => s + parseInt(r.billCount    || 0), 0);
    const totalRevenue  = data.reduce((s, r) => s + parseFloat(r.totalNet   || 0), 0);
    const totalDiscount = data.reduce((s, r) => s + parseFloat(r.totalDiscount || 0), 0);
    const avgBillAll    = totalBills > 0 ? totalRevenue / totalBills : 0;

    const summaryCards = [
        { label: 'Staff Members', value: data.length     },
        { label: 'Total Bills',   value: totalBills      },
        { label: 'Net Revenue',   value: fmt(totalRevenue)  },
        { label: 'Avg Bill',      value: fmt(avgBillAll) },
        { label: 'Discounts',     value: fmt(totalDiscount) },
    ];

    // chart data — top 8 by net revenue
    const chartData = data.slice(0, 8).map(r => ({
        name: r.staffName?.length > 10 ? r.staffName.slice(0, 10) + '…' : r.staffName,
        net:  parseFloat(r.totalNet || 0),
        bills: parseInt(r.billCount || 0),
    }));

    // rank badge color
    const rankColor = (i) => i === 0 ? '#F5A623' : i === 1 ? '#A8A8A8' : i === 2 ? '#C97D3A' : '#EBEBEB';
    const rankText  = (i) => i === 0 ? '#fff'    : i === 1 ? '#fff'    : i === 2 ? '#fff'    : '#888';

    return (
        <div className='w-screen h-screen hidden sm:flex sm:flex-col overflow-hidden'>
            <SherDineNav sideNav={(s) => setSideNav(s)} falseState={falseState} />
            <div className='w-full bg-[#f5f5f5] flex-grow flex gap-3 overflow-hidden'>
                <div className='w-max h-full z-[9999]'>
                    <SideNav focus={'Report'} state={sideNav} falseState={(s) => setFalse(s)} />
                </div>
                <div className='w-full h-full flex flex-col pr-3 pl-0 pb-3 pt-0 gap-3 overflow-hidden'>
                    {/* Breadcrumb */}
                    <div className='pr-3 h-max w-full bg-[#fff] borderBottom'>
                        <TopRootDiv image={'Report'} root={['Report']} section={'Staff Performance'} />
                    </div>

                    <div className='w-full h-full overflow-auto p-3 flex flex-col gap-3 font-nunito'>

                        {/* Controls */}
                        <div className='w-full flex flex-wrap gap-2 items-end bg-white rounded-xl p-3 shadow-sm border border-[#EBEBEB]'>
                            <div className='flex flex-col gap-1'>
                                <label className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>From</label>
                                <input type='date' value={dates.sdate}
                                    onChange={e => setDates(d => ({ ...d, sdate: e.target.value }))}
                                    className='border border-[#D5D5D5] rounded-lg px-3 py-1.5 text-[.85rem] focus:outline-none focus:ring-2 focus:ring-[#0008B4]/30' />
                            </div>
                            <div className='flex flex-col gap-1'>
                                <label className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>To</label>
                                <input type='date' value={dates.edate}
                                    onChange={e => setDates(d => ({ ...d, edate: e.target.value }))}
                                    className='border border-[#D5D5D5] rounded-lg px-3 py-1.5 text-[.85rem] focus:outline-none focus:ring-2 focus:ring-[#0008B4]/30' />
                            </div>
                            <button onClick={fetchData}
                                className='px-5 py-1.5 bg-[#0008B4] text-white rounded-lg text-[.85rem] font-semibold hover:bg-[#0006a0] transition-colors'>
                                Search
                            </button>
                            <div className='flex-1' />
                            <button onClick={() => exportToCSV(`Staff_Performance_${dates.sdate}`, STAFF_COLS, data)}
                                disabled={data.length === 0}
                                className='px-4 py-1.5 border border-[#D5D5D5] rounded-lg text-[.8rem] font-semibold text-[#464255] hover:bg-[#f5f5f5] transition-colors disabled:opacity-40'>
                                Export CSV
                            </button>
                            <button onClick={handlePrint}
                                disabled={data.length === 0}
                                className='px-4 py-1.5 border border-[#D5D5D5] rounded-lg text-[.8rem] font-semibold text-[#464255] hover:bg-[#f5f5f5] transition-colors disabled:opacity-40'>
                                Print / PDF
                            </button>
                        </div>

                        {/* Summary cards */}
                        <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3'>
                            {summaryCards.map(({ label, value }) => (
                                <div key={label} className='bg-white rounded-xl p-4 shadow-sm border border-[#EBEBEB] flex flex-col gap-1'>
                                    <span className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>{label}</span>
                                    <span className='text-[1.1rem] font-bold text-[#0008B4]'>{value}</span>
                                </div>
                            ))}
                        </div>

                        {/* Bar chart — revenue by staff */}
                        {chartData.length > 0 && (
                            <div className='bg-white rounded-xl p-4 shadow-sm border border-[#EBEBEB]'>
                                <span className='text-[.85rem] font-semibold text-[#464255]'>Revenue by Staff Member</span>
                                <div className='mt-3 h-[160px]'>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <BarChart data={chartData} margin={{ top: 0, right: 8, bottom: 0, left: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#f0f0f0' />
                                            <XAxis dataKey='name' tick={{ fontSize: 9, fill: '#888' }} />
                                            <YAxis tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} tick={{ fontSize: 9, fill: '#888' }} />
                                            <Tooltip
                                                formatter={(v, name) => [name === 'net' ? fmt(v) : v, name === 'net' ? 'Net Revenue' : 'Bills']}
                                            />
                                            <Bar dataKey='net' radius={[4,4,0,0]}>
                                                {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {/* Leaderboard table */}
                        <div className='bg-white rounded-xl shadow-sm border border-[#EBEBEB] overflow-hidden flex-1 min-h-0'>
                            {loading ? (
                                <div className='w-full h-48 flex items-center justify-center text-[#888]'>Loading…</div>
                            ) : data.length === 0 ? (
                                <div className='w-full h-48 flex items-center justify-center text-[#888]'>No data for selected range</div>
                            ) : (
                                <div className='overflow-auto h-full'>
                                    <table className='w-full text-[.82rem]'>
                                        <thead className='sticky top-0'>
                                            <tr className='bg-[#0008B4] text-white'>
                                                <th className='text-left px-3 py-2.5 font-semibold w-8'>#</th>
                                                <th className='text-left px-3 py-2.5 font-semibold'>Staff / Waiter</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Bills</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Net Revenue</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Avg Bill</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Discount</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Dine In</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Take Away</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Cash</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Card</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Online</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((row, i) => (
                                                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f9f9f9]'}>
                                                    <td className='px-3 py-2'>
                                                        <span className='inline-flex items-center justify-center w-5 h-5 rounded-full text-[.7rem] font-bold'
                                                            style={{ background: rankColor(i), color: rankText(i) }}>
                                                            {i + 1}
                                                        </span>
                                                    </td>
                                                    <td className='px-3 py-2 font-semibold text-[#464255]'>{row.staffName}</td>
                                                    <td className='px-3 py-2 text-right text-[#464255]'>{row.billCount}</td>
                                                    <td className='px-3 py-2 text-right font-bold text-[#0008B4]'>{fmt(row.totalNet)}</td>
                                                    <td className='px-3 py-2 text-right text-[#464255]'>{fmt(row.avgBill)}</td>
                                                    <td className='px-3 py-2 text-right text-[#c30000]'>{parseFloat(row.totalDiscount) > 0 ? fmt(row.totalDiscount) : '—'}</td>
                                                    <td className='px-3 py-2 text-right text-[#464255]'>{row.dineInCount}</td>
                                                    <td className='px-3 py-2 text-right text-[#464255]'>{row.takeAwayCount}</td>
                                                    <td className='px-3 py-2 text-right text-[#57BEB5]'>{parseFloat(row.cashTotal) > 0 ? fmt(row.cashTotal) : '—'}</td>
                                                    <td className='px-3 py-2 text-right text-[#57BEB5]'>{parseFloat(row.cardTotal) > 0 ? fmt(row.cardTotal) : '—'}</td>
                                                    <td className='px-3 py-2 text-right text-[#AE59DC]'>{parseFloat(row.onlineTotal) > 0 ? fmt(row.onlineTotal) : '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* Hidden print target */}
            <div className='hidden'>
                <PrintView ref={printRef} data={data} dates={dates} summary={summaryCards} />
            </div>
        </div>
    );
}

export default StaffPerformance;
